import { Button, Modal, ModalOverlay, ModalContent, ModalFooter, ModalBody, ModalCloseButton, Text, Input, Flex, Box } from '@chakra-ui/react'
import useMobile from '../../hooks/useMobile'
import { useState, useEffect } from 'react'
import InvalidInput from '../input/InvalidInput'

const TimeEditModal = ({
  isOpen,
  title,
  label,
  onClose,
  onSave,
  initialValue,
  isLoading,
  requireEndTime = false,
  onEndTimeSave,
  initialEndValue,
}: {
  title: string
  label: string
  isOpen: boolean
  onClose: () => void
  onSave: (v: string) => void
  initialValue: string
  isLoading: boolean
  requireEndTime?: boolean
  onEndTimeSave?: (v: string) => void
  initialEndValue?: string
}) => {
  const isMobile = useMobile(600)
  const [startTime, setStartTime] = useState(initialValue || '')
  const [endTime, setEndTime] = useState(initialEndValue || '')
  const [error, setError] = useState('')

  useEffect(() => {
    setStartTime(initialValue || '')
    setEndTime(initialEndValue || '')
  }, [initialValue, initialEndValue])

  const validateTime = (time: string): boolean => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
    return timeRegex.test(time)
  }

  const handleSave = () => {
    setError('')
    
    if (!startTime || !validateTime(startTime)) {
      setError('Please enter a valid start time (HH:MM format)')
      return
    }

    if (requireEndTime) {
      if (!endTime || !validateTime(endTime)) {
        setError('Please enter a valid end time (HH:MM format)')
        return
      }

      // Convert to 24-hour format for comparison
      const [startHour, startMin] = startTime.split(':').map(Number)
      const [endHour, endMin] = endTime.split(':').map(Number)
      const startMinutes = startHour * 60 + startMin
      const endMinutes = endHour * 60 + endMin

      if (endMinutes <= startMinutes) {
        setError('End time must be after start time')
        return
      }

      onSave(startTime)
      if (onEndTimeSave) {
        onEndTimeSave(endTime)
      }
    } else {
      onSave(startTime)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody p="0">
          <Text variant="title" fontSize="2xl" fontWeight="500" mb="20px">
            {title}
          </Text>

          <Text variant="title" fontSize={isMobile ? 'md' : 'lg'} lineHeight="19px" mt="25px" mb="10px">
            {label}
          </Text>
          
          <Flex gap="15px" alignItems="center" mb="15px">
            <Box flex="1">
              <Text variant="subtle" fontSize="sm" mb="5px">
                Start Time
              </Text>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                placeholder="HH:MM"
              />
            </Box>
            {requireEndTime && (
              <Box flex="1">
                <Text variant="subtle" fontSize="sm" mb="5px">
                  End Time
                </Text>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  placeholder="HH:MM"
                />
              </Box>
            )}
          </Flex>
          
          <InvalidInput isError={error !== ''} override={error} />
        </ModalBody>
        <ModalFooter>
          <Button variant="heavy" size="sm" isLoading={isLoading} onClick={handleSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default TimeEditModal

